import React, { useState, useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import styles from './Header.module.css';

const Header = () => {
    const [activeDropdown, setActiveDropdown] = useState(null);
    const [closeTimeout, setCloseTimeout] = useState(null);
    const [isTopHeaderHidden, setIsTopHeaderHidden] = useState(false);
    const [lastScrollY, setLastScrollY] = useState(0);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const handleMouseEnter = (menu) => {
        // Clear any existing timeout
        if (closeTimeout) {
            clearTimeout(closeTimeout);
            setCloseTimeout(null);
        }
        setActiveDropdown(menu);
    };

    const handleMouseLeave = () => {
        // Set a small delay before closing to allow mouse to enter modal
        const timeout = setTimeout(() => {
            setActiveDropdown(null);
        }, 100);
        setCloseTimeout(timeout);
    };

    const handleModalMouseEnter = () => {
        // Clear timeout when mouse enters modal
        if (closeTimeout) {
            clearTimeout(closeTimeout);
            setCloseTimeout(null);
        }
    };

    const handleModalMouseLeave = () => {
        // Close modal when leaving modal area
        setActiveDropdown(null);
    };


    const toggleMobileMenu = () => {
        setIsMobileMenuOpen(!isMobileMenuOpen);
        setActiveDropdown(null); // Close any open dropdowns when opening mobile menu
    };

    const handleMobileItemClick = (item) => {
        if (item.hasDropdown) {
            setActiveDropdown(activeDropdown === item.title ? null : item.title);
        } else {
            setIsMobileMenuOpen(false);
        }
    };

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (closeTimeout) {
                clearTimeout(closeTimeout);
            }
        };
    }, [closeTimeout]);

    // Track TopHeader visibility for Header positioning
    useEffect(() => {
        const handleScroll = () => {
            if (typeof window === 'undefined') return;
            
            const currentScrollY = window.scrollY;
            
            // Hide TopHeader when scrolling down and past 50px, show when scrolling up
            if (currentScrollY > lastScrollY && currentScrollY > 50) {
                setIsTopHeaderHidden(true);
            } else if (currentScrollY < lastScrollY) {
                setIsTopHeaderHidden(false);
            }
            
            setLastScrollY(currentScrollY);
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, [lastScrollY]);

    const navigationItems = [
        { 
            title: 'ABOUT US', 
            href: '/about', 
            hasDropdown: true,
            hasSpecialModal: true,
            dropdownItems: [
                { title: 'Our Company', href: '/about/company' },
                { title: 'Our Team', href: '/about/team' },
                { title: 'Our Vision', href: '/about/vision' }
            ]
        },
        { title: 'PROJECTS', href: '/projects', hasDropdown: false },
        { 
            title: 'VERTICALS & SERVICES', 
            href: '/services', 
            hasDropdown: true,
            hasSpecialModal: true,
            dropdownItems: [
                { title: 'EHV GIS & AIS Substations', href: '/services/substations', isDisable: true, description: 'Extra High Voltage Gas Insulated & Air Insulated Substations up to 400kV capacity.' },
                { title: 'Power Transmission & Distribution', href: '/services/transmission', isDisable: true, description: 'High Tension/Low Tension power transmission and distribution systems.' },
                { title: 'Underground Cable Projects', href: '/services/cables', isDisable: true, description: 'Underground cable installations up to 220kV with turnkey execution.' },
                { title: 'Industrial Electrification', href: '/services/industrial',isDisable: true, description: 'Complete electrical solutions for industrial infrastructure and facilities.' },
                { title: 'Tunnel Ventilation & SCADA', href: '/services/tunnel', isDisable: true, description: 'Advanced tunnel ventilation systems and SCADA solutions for railways and metros.' },
                { title: 'Turnkey EPC Projects', href: '/services/epc', isDisable: true, description: 'End-to-end engineering, procurement, and construction project delivery.' },
                { title: 'Data Centre', href: '/services/substations', isDisable: true, description: '' },
                { title: 'Power Infrastructure', href: '/services/substations', isDisable: true, description: '' },
            ]
        },
        { 
            title: 'OUR SUBSIDIARIES', 
            href: '/', 
            hasDropdown: true,
            hasSpecialModal: true,
            dropdownItems: [
                { title: 'Piscesia Savronik (JV)', href: '/', isDisable: true, description: 'Electrical and Mechanical solutions' }
            ]
        },
        // { 
        //     title: 'LIFE @PISCESIA', 
        //     href: '/life', 
        //     hasDropdown: true,
        //     hasSpecialModal: true,
        //     dropdownItems: [
        //         { title: 'PISCESIA Culture', href: '/life/culture', description: 'Our inclusive culture ensures that all employees feel respected, supported, and heard.' },
        //         { title: 'Careers', href: '/life/careers', description: 'Explore the exciting career opportunities at PISCESIA and step towards impactful career.' }
        //     ]
        // },
        // { title: 'CSR', href: '/csr', hasDropdown: false },
        // { 
        //     title: 'RESOURCES', 
        //     href: '/resources', 
        //     hasDropdown: true,
        //     hasSpecialModal: true,
        //     dropdownItems: [
        //         { title: 'Our Blogs', href: '/resources/blogs', description: 'Discover the latest industry trends, innovations, market shifts, and insights to stay informed.' },
        //         { title: 'Newsroom', href: '/resources/newsroom', description: 'Stay updated with the latest industry news, trends, and innovations.' }
        //     ]
        // },
        { title: 'CONTACT', href: '/contact', hasDropdown: false }
    ];

    return (
        <>
            {/* Backdrop blur when modal is open */}
            {activeDropdown && (
                <div className={styles.modalBackdrop}></div>
            )}
            
            <header className={`${styles.header} ${isTopHeaderHidden ? styles.topHeaderHidden : ''}`}>
            <div className={styles.container}>
                {/* Logo Section */}
                <div className={styles.logoSection}>
                    <Link href="/" className={styles.logoLink}>
                        <div className={styles.logoContainer}>
                            <Image
                                src="/logos/main_logo.png"
                                alt="PISCESIA Logo"
                                width={120}
                                height={60}
                                className={styles.mainLogo}
                                priority
                            />
                        </div>
                    </Link>
                </div>

                {/* Hamburger Menu Button */}
                <button 
                    className={styles.hamburgerButton}
                    onClick={toggleMobileMenu}
                    aria-label="Toggle mobile menu"
                >
                    <span className={`${styles.hamburgerLine} ${isMobileMenuOpen ? styles.hamburgerActive : ''}`}></span>
                    <span className={`${styles.hamburgerLine} ${isMobileMenuOpen ? styles.hamburgerActive : ''}`}></span>
                    <span className={`${styles.hamburgerLine} ${isMobileMenuOpen ? styles.hamburgerActive : ''}`}></span>
                </button>

                {/* Desktop Navigation Section */}
                <nav className={styles.navigation}>
                    <ul className={styles.navList}>
                        {navigationItems.map((item, index) => (
                            <li 
                                key={index} 
                                className={styles.navItem}
                                data-has-modal={item.hasSpecialModal ? "true" : "false"}
                                data-no-dropdown={!item.hasDropdown ? "true" : "false"}
                                onMouseEnter={() => item.hasDropdown && handleMouseEnter(item.title)}
                                onMouseLeave={() => item.hasDropdown && handleMouseLeave()}
                            >
                                {item.hasDropdown ? (
                                    <span className={styles.navLink}>
                                    <span>{item.title}</span>
                                        <svg 
                                            className={`${styles.dropdownArrow} ${activeDropdown === item.title ? styles.active : ''}`}
                                            width="12" 
                                            height="12" 
                                            viewBox="0 0 12 12"
                                        >
                                            <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    </span>
                                ) : (
                                    <Link href={item.href} className={styles.navLink}>
                                        <span>{item.title}</span>
                                    </Link>
                                    )}
                                
                                {item.hasDropdown && activeDropdown === item.title && (
                                    <>
                                        {item.hasSpecialModal && item.title === 'ABOUT US' ? (
                                            <div 
                                                className={`${styles.aboutModal} ${isTopHeaderHidden ? styles.topHeaderHidden : ''}`}
                                                onMouseEnter={handleModalMouseEnter}
                                                onMouseLeave={handleModalMouseLeave}
                                            >
                                                <div className={styles.modalContent}>
                                                    <div className={styles.modalLeft}>
                                                        <div className={styles.modalImage}>
                                                            <Image
                                                                src="/images/pexels-thisisengineering-3862135.jpg" 
                                                                alt="PISCESIA Office" 
                                                                width={300}
                                                                height={280}
                                                                className={styles.officeImage}
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className={styles.modalRight}>
                                                        <div className={styles.modalHeader}>
                                                            <h3 className={styles.modalTitle}>Experience the PISCESIA Journey</h3>
                                                        </div>
                                                        <div className={styles.modalGrid}>
                                                            <div className={styles.modalSection}>
                                                                <div className={styles.sectionIcon}>
                                                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                                                        <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                                        <path d="M2 17L12 22L22 17" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                                        <path d="M2 12L12 17L22 12" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                                    </svg>
                                                                </div>
                                                                <Link href="/about/story" className={styles.sectionLink}>
                                                                    <h4 className={styles.sectionTitle}>PISCESIA Story</h4>
                                                                    <p className={styles.sectionText}>At PISCESIA, we believe innovation paves the way for endless possibilities.</p>
                                                                    <span className={styles.sectionArrow}>→</span>
                                                                </Link>
                                                            </div>
                                                            
                                                            <div className={styles.modalSection}>
                                                                <div className={styles.sectionIcon}>
                                                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                                                        <path d="M9 11H15M9 15H15M17 21L20 18L17 15M7 21L4 18L7 15M2 5C2 4.46957 2.21071 3.96086 2.58579 3.58579C2.96086 3.21071 3.46957 3 4 3H20C20.5304 3 21.0391 3.21071 21.4142 3.58579C21.7893 3.96086 22 4.46957 22 5V13C22 13.5304 21.7893 14.0391 21.4142 14.4142C21.0391 14.7893 20.5304 15 20 15H4C3.46957 15 2.96086 14.7893 2.58579 14.4142C2.21071 14.0391 2 13.5304 2 13V5Z" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                                    </svg>
                                                                </div>
                                                                {/* <Link href="/about/vision" className={styles.sectionLink}> */}
                                                                    <h4 className={styles.sectionTitle}>Vision & Mission</h4>
                                                                    <p className={styles.sectionText}>We grow our business through relentless client focus, operational excellence.</p>
                                                                    <span className={styles.sectionArrow}>→</span>
                                                                {/* </Link> */}
                                                            </div>
                                                            
                                                            <div className={styles.modalSection}>
                                                                <div className={styles.sectionIcon}>
                                                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                                                        <path d="M16 21V19C16 17.9391 15.5786 16.9217 14.8284 16.1716C14.0783 15.4214 13.0609 15 12 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21M12.5 7C12.5 9.20914 10.7091 11 8.5 11C6.29086 11 4.5 9.20914 4.5 7C4.5 4.79086 6.29086 3 8.5 3C10.7091 3 12.5 4.79086 12.5 7ZM20.5 8L22 12H17L18.5 8ZM18.5 8V6C18.5 5.73478 18.6054 5.48043 18.7929 5.29289C18.9804 5.10536 19.2348 5 19.5 5C19.7652 5 20.0196 5.10536 20.2071 5.29289C20.3946 5.48043 20.5 5.73478 20.5 6V8H18.5Z" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                                    </svg>
                                                                </div>
                                                                {/* <Link href="/about/leadership" className={styles.sectionLink}> */}
                                                                    <h4 className={styles.sectionTitle}>Leadership</h4>
                                                                    <p className={styles.sectionText}>Our leadership team is the cornerstone of our success.</p>
                                                                    <span className={styles.sectionArrow}>→</span>
                                                                {/* </Link> */}
                                                            </div>
                                                            
                                                            {/* <div className={styles.modalSection}>
                                                                <div className={styles.sectionIcon}>
                                                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                                                        <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                                        <path d="M14 2V8H20" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                                        <path d="M16 13H8" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                                        <path d="M16 17H8" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                                        <path d="M10 9H9H8" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                                    </svg>
                                                                </div>
                                                                <Link href="/about/certifications" className={styles.sectionLink}>
                                                                    <h4 className={styles.sectionTitle}>Certifications</h4>
                                                                    <p className={styles.sectionText}>Commitment to quality, safety, and industry excellence.</p>
                                                                    <span className={styles.sectionArrow}>→</span>
                                                                </Link>
                                                            </div> */}
                                                        </div>
                                                    </div>

                                                </div>
                                            </div>
                                        ) : item.title === 'VERTICALS & SERVICES' ? (
                                            <div 
                                                className={`${styles.servicesModal} ${isTopHeaderHidden ? styles.topHeaderHidden : ''}`}
                                                onMouseEnter={handleModalMouseEnter}
                                                onMouseLeave={handleModalMouseLeave}
                                            >
                                                <div className={styles.modalContent}>
                                                    <div className={styles.modalLeft}>
                                                        <div className={styles.modalImage}>
                                                            <Image
                                                                src="/images/pexels-thisisengineering-3862135.jpg" 
                                                                alt="PISCESIA Office" 
                                                                width={300}
                                                                height={280}
                                                                className={styles.officeImage}
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className={styles.modalRight}>
                                                        <div className={styles.modalHeader}>
                                                            <h3 className={styles.modalTitle}>Our Engineering Solutions</h3>
                                                        </div>
                                                        <div className={styles.modalGrid}>
                                                            {item.dropdownItems.map((service, serviceIndex) => (
                                                                <div key={serviceIndex} className={`${styles.modalSection} ${service.isDisable ? "disabledLink" : ""}` }>
                                                                    <div className={styles.sectionIcon}>
                                                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                                                            <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#0ea5e9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                                        </svg>
                                                                    </div>
                                                                    {/* <Link href={service.href} className={styles.sectionLink}> */}
                                                                        <h4 className={styles.sectionTitle}>{service.title}</h4>
                                                                        <p className={styles.sectionText}>{service.description}</p>
                                                                        <span className={styles.sectionArrow}>→</span>
                                                                    {/* </Link> */}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ) : item.title === 'OUR SUBSIDIARIES' ? (
                                            <div 
                                                className={`${styles.servicesModal} ${isTopHeaderHidden ? styles.topHeaderHidden : ''}`}
                                                onMouseEnter={handleModalMouseEnter}
                                                onMouseLeave={handleModalMouseLeave}
                                            >
                                                <div className={styles.modalContent}>
                                                    <div className={styles.modalLeft}>
                                                        <div className={styles.modalImage}>
                                                            <Image
                                                                src="/images/pexels-thisisengineering-3862135.jpg" 
                                                                alt="PISCESIA Office" 
                                                                width={300}
                                                                height={280}
                                                                className={styles.officeImage}
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className={styles.modalRight}>
                                                        <div className={styles.modalHeader}>
                                                            <h3 className={styles.modalTitle}>Our Assosiate Company</h3>
                                                        </div>
                                                        <div className={styles.modalGrid}>
                                                            {item.dropdownItems.slice(0, 4).map((service, serviceIndex) => (
                                                                <div key={serviceIndex} className={`${styles.modalSection} ${service.isDisable ? "disabledLink" : ""}` }>
                                                                    <div className={styles.sectionIcon}>
                                                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                                                            <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#0ea5e9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                                        </svg>
                                                                    </div>
                                                                    {/* <Link href={service.href} className={styles.sectionLink}> */}
                                                                        <h4 className={styles.sectionTitle}>{service.title}</h4>
                                                                        <p className={styles.sectionText}>{service.description}</p>
                                                                        <span className={styles.sectionArrow}>→</span>
                                                                    {/* </Link> */}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ) : item.title === 'LIFE @PISCESIA' ? (
                                            <div 
                                                className={`${styles.lifeModal} ${isTopHeaderHidden ? styles.topHeaderHidden : ''}`}
                                                onMouseEnter={handleModalMouseEnter}
                                                onMouseLeave={handleModalMouseLeave}
                                            >
                                                <div className={styles.modalContent}>
                                                    <div className={styles.modalLeft}>
                                                        <div className={styles.modalImage}>
                                                            <Image
                                                                src="/images/pexels-thisisengineering-3862135.jpg" 
                                                                alt="PISCESIA Office" 
                                                                width={300}
                                                                height={280}
                                                                className={styles.officeImage}
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className={styles.modalRight}>
                                                        <div className={styles.modalHeader}>
                                                            <h3 className={styles.modalTitle}>We cultivate an inclusive work environment</h3>
                                                        </div>
                                                        <div className={styles.modalGrid}>
                                                            {item.dropdownItems.slice(0, 4).map((lifeItem, lifeIndex) => (
                                                                <div key={lifeIndex} className={`${styles.modalSection} ${service.isDisable ? "disabledLink" : ""}`}>
                                                                    <div className={styles.sectionIcon}>
                                                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                                                            <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                                            <path d="M2 17L12 22L22 17" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                                            <path d="M2 12L12 17L22 12" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                                        </svg>
                                                                    </div>
                                                                    <Link href={lifeItem.href} className={styles.sectionLink}>
                                                                        <h4 className={styles.sectionTitle}>{lifeItem.title}</h4>
                                                                        <p className={styles.sectionText}>{lifeItem.description}</p>
                                                                        <span className={styles.sectionArrow}>→</span>
                                                                    </Link>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ) : item.title === 'RESOURCES' ? (
                                            <div 
                                                className={`${styles.resourcesModal} ${isTopHeaderHidden ? styles.topHeaderHidden : ''}`}
                                                onMouseEnter={handleModalMouseEnter}
                                                onMouseLeave={handleModalMouseLeave}
                                            >
                                                <div className={styles.modalContent}>
                                                    <div className={styles.modalLeft}>
                                                        <div className={styles.modalImage}>
                                                            <Image
                                                                src="/images/pexels-thisisengineering-3862135.jpg" 
                                                                alt="PISCESIA Office" 
                                                                width={300}
                                                                height={280}
                                                                className={styles.officeImage}
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className={styles.modalRight}>
                                                        <div className={styles.modalHeader}>
                                                            <h3 className={styles.modalTitle}>Explore the PISCESIA Insights updates</h3>
                                                        </div>
                                                        <div className={styles.modalGrid}>
                                                            {item.dropdownItems.slice(0, 4).map((resourceItem, resourceIndex) => (
                                                                <div key={resourceIndex} className={styles.modalSection + service.isDisable ? "disabledLink" : ""}>
                                                                    <div className={styles.sectionIcon}>
                                                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                                                            <path d="M4 19.5C4 18.837 4.26339 18.2011 4.73223 17.7322C5.20108 17.2634 5.83696 17 6.5 17H20" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                                            <path d="M6.5 2H20V22H6.5C5.83696 22 5.20108 21.7366 4.73223 21.2678C4.26339 20.7989 4 20.163 4 19.5V4.5C4 3.83696 4.26339 3.20108 4.73223 2.73223C5.20108 2.26339 5.83696 2 6.5 2Z" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                            </svg>
                                                                    </div>
                                                                    <Link href={resourceItem.href} className={styles.sectionLink}>
                                                                        <h4 className={styles.sectionTitle}>{resourceItem.title}</h4>
                                                                        <p className={styles.sectionText}>{resourceItem.description}</p>
                                                                        <span className={styles.sectionArrow}>→</span>
                                                        </Link>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            <div 
                                                className={styles.dropdown}
                                                onMouseEnter={handleModalMouseEnter}
                                                onMouseLeave={handleModalMouseLeave}
                                            >
                                                <ul className={styles.dropdownList}>
                                                    {item.dropdownItems.map((dropdownItem, dropdownIndex) => (
                                                        <li key={dropdownIndex} className={dropdownItem.isDisable ? styles.disabledLink : "" + styles.dropdownItem}>
                                                            <Link href={dropdownItem.href} className={styles.dropdownLink}>
                                                                {dropdownItem.title}
                                                            </Link>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </>
                                )}
                            </li>
                        ))}
                    </ul>
                </nav>
            </div>

            {/* Mobile Menu */}
            <div className={`${styles.mobileMenu} ${isMobileMenuOpen ? styles.mobileMenuOpen : ''}`}>
                <nav className={styles.mobileNavigation}>
                    {/* Top Header Items */}
                    {/* <div className={styles.mobileTopSection}>
                        <h3 className={styles.mobileTopTitle}>Quick Links</h3>
                        <div className={styles.mobileTopLinks}>
                            <Link href="/investors" className={styles.mobileTopLink} onClick={() => setIsMobileMenuOpen(false)}>
                                <span>Investors</span>
                                <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
                                    <path d="M1 9L11 1M11 1H3M11 1V9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </Link>
                            <Link href="/newsroom" className={styles.mobileTopLink} onClick={() => setIsMobileMenuOpen(false)}>
                                <span>Newsroom</span>
                                <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
                                    <path d="M1 9L11 1M11 1H3M11 1V9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </Link>
                            <Link href="/career" className={styles.mobileTopLink} onClick={() => setIsMobileMenuOpen(false)}>
                                <span>Career</span>
                                <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
                                    <path d="M1 9L11 1M11 1H3M11 1V9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </Link>
                        </div>
                    </div> */}
                    
                    <ul className={styles.mobileNavList}>
                        {navigationItems.map((item, index) => (
                            <li key={index} className={styles.mobileNavItem}>
                                {item.hasDropdown ? (
                                    <>
                                        <button 
                                            className={styles.mobileNavLink}
                                            onClick={() => handleMobileItemClick(item)}
                                        >
                                            <span>{item.title}</span>
                                            <svg 
                                                className={`${styles.mobileDropdownArrow} ${activeDropdown === item.title ? styles.mobileDropdownActive : ''}`}
                                                width="12" 
                                                height="12" 
                                                viewBox="0 0 12 12"
                                            >
                                                <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                        </button>
                                        {activeDropdown === item.title && (
                                            <ul className={styles.mobileDropdown}>
                                                {item.dropdownItems?.map((dropdownItem, dropdownIndex) => (
                                                    <li key={dropdownIndex} className={dropdownItem.isDisable ? styles.mobileDisabledLink : styles.mobileDropdownItem }>
                                                        <Link 
                                                            href={dropdownItem.href} 
                                                            className={styles.mobileDropdownLink}
                                                            onClick={() => setIsMobileMenuOpen(false)}
                                                        >
                                                            <div>
                                                                <div className={styles.mobileDropdownTitle}>{dropdownItem.title}</div>
                                                                <div className={styles.mobileDropdownDesc}>{dropdownItem.description}</div>
                                                            </div>
                                                        </Link>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </>
                                ) : (
                                    <Link 
                                        href={item.href} 
                                        className={styles.mobileNavLink}
                                        onClick={() => setIsMobileMenuOpen(false)}
                                    >
                                        {item.title}
                                    </Link>
                                )}
                            </li>
                        ))}
                    </ul>
                </nav>
            </div>
        </header>
        </>
    );
};

export default Header;